<template>
  <SetupBulkUpload
    v-model="dialog"
    show-trigger
    trigger-class="mr-1 px-4 py-1"
    heading="Bulk Upload"
    :test-ids="shellTestIds"
    :step1-description="step1Description"
    :step2-description="step2Description"
    :has-preview="hasUploadPreview"
    :validating="isFileProcessing"
    :file-name="selectedFileName"
    :reupload-visible="hasUploadPreview || isFileProcessing || uploadBlockingErrors"
    :file="filesbulk"
    :reset-dropzone="resetTrigger"
    :show-inline-errors="uploadBlockingErrors"
    :inline-error-text="bulkUploadErrorFooterText"
    :header-error-messages="headerErrorMessages"
    :data-error-messages="inlineDataErrorMessages"
    :footer-visible="showBulkErrorFooter || hasFileDataError || dataOnlyWithError"
    :error-footer-visible="showBulkErrorFooter"
    :error-footer-text="bulkUploadErrorFooterText"
    :error-filter-visible="hasFileDataError || dataOnlyWithError"
    :show-errors-only="dataOnlyWithError"
    :busy="loading"
    :can-submit="canSubmitUpload"
    @open="openBulkUpload"
    @cancel="onShellCancel"
    @download="downloadTemplate"
    @files-change="updateFilesBulk"
    @reupload="reuploadDoc"
    @upload="uploadData"
    @update:showErrorsOnly="dataOnlyWithError = $event"
  >
    <template #preview>
      <div
        class="bulk-import-dialog__legacy-table"
        data-testid="financialclose_component_infomanager_bulkupload__table"
      >
        <v-simple-table data-testid="financialclose_component_infomanager_bulkupload__simple-table">
          <template v-slot:default>
            <thead class="tableHeaderBg">
              <tr>
                <th
                  v-for="header in tableheader"
                  :key="header.value"
                  class="text-center tableHeaderBg"
                >
                  {{ header.name }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in filteredPreviewRows"
                :key="'row-' + item.index"
                class="bulk-import-dialog__row"
                :data-testid="`financialclose_component_infomanager_bulkupload__table-row--${item.index}`"
              >
                <td
                  v-for="header in tableheader"
                  :key="'col-' + header.value + '-' + item.index"
                  class="px-4 single-line"
                  :class="previewCellClass(item.index, header)"
                  :data-testid="`financialclose_component_infomanager_bulkupload__table-cell-${header.value}`"
                >
                  <span
                    v-if="header.value === 'uploadlink' && cellError(item.index, 'uploadlink')"
                    class="error--text bulk-import-dialog__cell--truncate"
                  >
                    {{ cellError(item.index, 'uploadlink') }}
                  </span>
                  <v-tooltip
                    v-else-if="cellError(item.index, header.value)"
                    bottom
                    content-class="tooltip-bottom"
                  >
                    <template #activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                        v-on="on"
                        class="bulk-import-dialog__cell-inner bulk-import-dialog__cell-inner--error error--text"
                      >
                        <span class="bulk-import-dialog__cell--truncate">
                          {{ cellError(item.index, header.value) }}
                        </span>
                      </div>
                    </template>
                    <span>{{ cellError(item.index, header.value) }}</span>
                  </v-tooltip>

                  <div v-else-if="header.value === 'sno'">
                    {{ item.index + 1 }}
                  </div>

                  <OverflowList
                    v-else-if="header.value === 'checklistref'"
                    :items="item.row.checklistref || []"
                    :data-testid="`financialclose_component_infomanager_bulkupload__overflow-badge--${item.index}`"
                  />

                  <span
                    v-else
                    :class="header.value === 'requested_info' ? 'bulk-import-dialog__cell--truncate' : ''"
                  >
                    {{ item.row[header.value] }}
                  </span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <div
        v-if="infoNotes.length"
        class="mt-1"
        data-testid="financialclose_component_infomanager_bulkupload__info-notes"
      >
        <div
          v-for="(note, idx) in infoNotes"
          :key="'note-' + idx"
          class="font-size-xm"
          :data-testid="`financialclose_component_infomanager_bulkupload__info-note-${idx}`"
        >
          {{ note }}
        </div>
      </div>

      <div
        v-if="dataErrorMessages.length"
        class="mt-1"
        data-testid="financialclose_component_infomanager_bulkupload__preview-data-errors"
      >
        <div
          v-for="(err, idx) in dataErrorMessages"
          :key="idx"
          class="error--text font-size-xm"
          :data-testid="`financialclose_component_infomanager_bulkupload__data-error-${idx}`"
        >
          {{ idx + 1 }}. {{ err }}
        </div>
      </div>
    </template>
  </SetupBulkUpload>
</template>

<script>
import { OverflowList, SetupBulkUpload } from '@/modules/financialclose/components';
import {
  sanitizePriority,
  sanitizeType,
  trimInput,
  sanitizeChecklistRef,
  formatRequestTypeLabel,
  validateDueDays,
  validateEmail,
  validateUploadLink,
} from '@/modules/financialclose/utils/bulkUploadValidation';
import { ERROR_MESSAGES, IM_REQUEST_TYPE_CODES } from '@/modules/financialclose/utils/constants';

const EXPECTED_HEADERS = [
  'Request Information',
  'Type',
  'Link',
  'Classification',
  'Requested From',
  'Email ID',
  'Tasks Linked',
  'Priority',
  'Due Days',
];

const SHELL_TEST_IDS = {
  root: 'financialclose_component_infomanager_bulkupload',
  openButton: 'financialclose_component_infomanager_bulkupload__open-btn',
  drawer: 'financialclose_component_infomanager_bulkupload__drawer',
  reuploadInput: 'financialclose_component_infomanager_bulkupload__reupload-input',
  body: 'financialclose_component_infomanager_bulkupload__drawer-body',
  step1: 'financialclose_component_infomanager_bulkupload__step-1',
  download: 'financialclose_component_infomanager_bulkupload__download-template-btn',
  step2: 'financialclose_component_infomanager_bulkupload__step-2',
  reuploadButton: 'financialclose_component_infomanager_bulkupload__reupload-btn',
  validating: 'financialclose_component_infomanager_bulkupload__validating',
  inlineErrors: 'financialclose_component_infomanager_bulkupload__inline-errors',
  inlineErrorMessage: 'financialclose_component_infomanager_bulkupload__error-msg',
  headerErrors: 'financialclose_component_infomanager_bulkupload__header-errors',
  headerErrorsList: 'financialclose_component_infomanager_bulkupload__header-errors-list',
  headerError: 'financialclose_component_infomanager_bulkupload__header-error',
  rowErrors: 'financialclose_component_infomanager_bulkupload__data-errors',
  dataError: 'financialclose_component_infomanager_bulkupload__data-error',
  dropzone: 'financialclose_component_infomanager_bulkupload__dragdrop',
  dragDrop: 'financialclose_component_infomanager_bulkupload__drag-drop-file',
  preview: 'financialclose_component_infomanager_bulkupload__preview-table-container',
  errorFilterRow: 'financialclose_component_infomanager_bulkupload__error-filter-section',
  errorFilter: 'financialclose_component_infomanager_bulkupload__show-errors-checkbox',
  errorFooter: 'financialclose_component_infomanager_bulkupload__error-footer',
  errorFooterMessage: 'financialclose_component_infomanager_bulkupload__error-footer',
  cancel: 'financialclose_component_infomanager_bulkupload__cancel-button',
  upload: 'financialclose_component_infomanager_bulkupload__upload-button',
};

export default {
  name: 'BulkUploadInformationManager',
  components: {
    OverflowList,
    SetupBulkUpload,
  },
  props: {
    checklist: {
      type: Array,
      default: () => [],
    },
    infoOwners: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      shellTestIds: SHELL_TEST_IDS,
      step1Description: 'Start by downloading the bulk upload Excel template for information requests.',
      step2Description: 'Fill in the Excel file, then drag and drop it here to create information requests.',
      dialog: false,
      file: null,
      fileprocessing: {},
      uploaddata: [],
      dataerror: [],
      loading: false,
      filesbulk: [],
      resetTrigger: false,
      tableheader: [
        { name: 'S No', value: 'sno' },
        { name: 'Request Information', value: 'requested_info' },
        { name: 'Type', value: 'type' },
        { name: 'Link', value: 'uploadlink' },
        { name: 'Classification', value: 'classification' },
        { name: 'Requested From', value: 'info_owner_name' },
        { name: 'Email ID', value: 'info_owner_email' },
        { name: 'Tasks Linked (Refs)', value: 'checklistref' },
        { name: 'Priority', value: 'priority' },
        { name: 'Due Days', value: 'duedate' },
      ],
      dataOnlyWithError: false,
      errorFlag: false,
      parseGeneration: 0,
      headerErrorMessages: [],
      dataErrorMessages: [],
      infoNotes: [],
    };
  },
  computed: {
    hasUploadPreview() {
      return (this.uploaddata || []).length > 0;
    },
    isFileProcessing() {
      return this.loading && !this.hasUploadPreview;
    },
    selectedFileName() {
      const current = Array.isArray(this.filesbulk) ? this.filesbulk[0] : this.filesbulk;
      return current?.name || '';
    },
    hasFileDataError() {
      return (this.dataerror || []).some((row) => row && Object.keys(row).length > 0);
    },
    uploadBlockingErrors() {
      return !this.hasUploadPreview && (this.headerErrorMessages.length > 0 || this.dataErrorMessages.length > 0);
    },
    inlineDataErrorMessages() {
      return this.uploadBlockingErrors ? this.dataErrorMessages : [];
    },
    showBulkErrorFooter() {
      return this.hasUploadPreview && this.errorFlag;
    },
    canSubmitUpload() {
      return this.hasUploadPreview && !this.errorFlag && !this.headerErrorMessages.length;
    },
    bulkUploadErrorFooterText() {
      return ERROR_MESSAGES.BULK_UPLOAD_ERROR_FOOTER;
    },
    filteredPreviewRows() {
      return (this.uploaddata || [])
        .map((row, index) => ({
          row,
          index,
          errors: this.dataerror[index] || {},
        }))
        .filter((item) => {
          if (!this.dataOnlyWithError) return true;
          return Object.keys(item.errors).length > 0;
        });
    },
  },
  beforeDestroy() {
    this.resetData();
  },
  methods: {
    openBulkUpload() {
      this.resetData();
      this.dialog = true;
    },
    onShellCancel() {
      this.dialog = false;
      this.resetData();
    },
    downloadTemplate() {
      this.$nova.downloadFile('', {}, '/v2/financialclose/masters/informationmanager/getbulksubmittemplate');
    },
    cellError(index, field) {
      if (field === 'sno') return '';
      return (this.dataerror[index] && this.dataerror[index][field]) || '';
    },
    previewCellClass(index, header) {
      const classes = [];
      if (header.value === 'requested_info') {
        classes.push('bulk-import-dialog__cell--truncate');
      }
      if (this.cellError(index, header.value)) {
        classes.push('bulk-import-dialog__cell--error');
      }
      return classes;
    },
    findExistingInfoOwner(email) {
      const key = (email == null ? '' : String(email)).trim().toLowerCase();
      if (!key) return null;
      return (this.infoOwners || []).find((owner) =>
        (owner.email || '').toString().trim().toLowerCase() === key
      ) || null;
    },
    validateAndMapTaskRefs(refs, rowNumber) {
      const tasklist = [];
      const errors = [];
      const warnings = [];

      if (!refs || refs.length === 0) {
        return { tasklist, errors, warnings };
      }

      if (!this.checklist || this.checklist.length === 0) {
        console.warn('[BulkUploadInformationManager]: Checklist prop is empty. Task references cannot be validated.');
        warnings.push(`Row ${rowNumber}: Task references provided but checklist data is unavailable for validation`);
        return { tasklist, errors, warnings };
      }

      refs.forEach((ref) => {
        const task = this.checklist.find((t) => t.ref === ref);
        if (task) {
          tasklist.push({ checklist_id: task._id });
        } else {
          errors.push(`Row ${rowNumber}: Task reference '${ref}' not found in masters`);
        }
      });

      return { tasklist, errors, warnings };
    },
    readFile(key) {
      const file = this.filesbulk[0];
      if ((file || {}).name && (this.fileprocessing[key] || 0) === 0) {
        this.loading = true;
        this.fileprocessing[key] = 1;
        this.parseGeneration += 1;
        const parseId = this.parseGeneration;
        const ext = ((file || {}).name || '').split('.').pop();

        Promise.resolve()
          .then(() => {
            if (['xlsx', 'xls', 'xlsm', 'xlsb'].indexOf(ext) === -1) {
              throw new Error('Only xlsx, xls, xlsm, xlsb file extension supported');
            }
            return this.$nova.readExcelFile(file, 'TOP SHEET');
          })
          .then((dt) => {
            if (parseId !== this.parseGeneration) return;
            const actualHeaders = dt[0] || [];

            if (JSON.stringify(actualHeaders) === JSON.stringify(EXPECTED_HEADERS)) {
              const filedata = [];
              const errordata = [];
              const validationErrors = [];
              const infoNotes = [];
              this.errorFlag = false;
              this.headerErrorMessages = [];
              this.dataErrorMessages = [];
              this.infoNotes = [];

              for (let i = 0; i < dt.length; i++) {
                if (i > 0 && dt[i].length > 0) {
                  const el = dt[i];
                  const rowNumber = i;
                  const typeResult = sanitizeType(el[1], { allowed: IM_REQUEST_TYPE_CODES });
                  const priorityResult = sanitizePriority(el[7]);
                  const checklistRefs = sanitizeChecklistRef(el[6]);
                  const uploadlink = trimInput(el[2]);
                  const taskValidation = this.validateAndMapTaskRefs(checklistRefs, rowNumber);
                  const emailResult = validateEmail(el[5], { required: true });
                  const dueDaysResult = validateDueDays(el[8], { required: false });

                  const uploadedName = trimInput(el[4]);
                  const existingOwner = this.findExistingInfoOwner(emailResult.value);
                  const canonicalName = existingOwner
                    ? trimInput(existingOwner.name)
                    : uploadedName;

                  const rowdata = {
                    requested_info: trimInput(el[0]),
                    type: formatRequestTypeLabel(typeResult.value) || typeResult.value,
                    _type_code: typeResult.value,
                    uploadlink,
                    classification: trimInput(el[3]),
                    info_owner_name: canonicalName,
                    info_owner_email: emailResult.value,
                    checklistref: checklistRefs,
                    _tasklist: taskValidation.tasklist,
                    priority: priorityResult.value,
                    duedate: dueDaysResult.value,
                  };

                  if (existingOwner && uploadedName && uploadedName !== canonicalName) {
                    infoNotes.push(
                      `Row ${rowNumber}: Requested From was set to the existing owner name '${canonicalName}'`
                    );
                  }

                  const errors = {};

                  if (rowdata.classification === '') {
                    errors.classification = ERROR_MESSAGES.CLASSIFICATION_REQUIRED;
                    validationErrors.push(`Row ${rowNumber}: ${ERROR_MESSAGES.CLASSIFICATION_REQUIRED}`);
                  }
                  if (rowdata.requested_info === '') {
                    errors.requested_info = ERROR_MESSAGES.REQUESTED_INFORMATION_REQUIRED;
                    validationErrors.push(`Row ${rowNumber}: ${ERROR_MESSAGES.REQUESTED_INFORMATION_REQUIRED}`);
                  }
                  if (!existingOwner && rowdata.info_owner_name === '') {
                    errors.info_owner_name = ERROR_MESSAGES.REQUESTED_FROM_REQUIRED;
                    validationErrors.push(`Row ${rowNumber}: ${ERROR_MESSAGES.REQUESTED_FROM_REQUIRED}`);
                  }
                  if (emailResult.error) {
                    errors.info_owner_email = emailResult.error;
                    validationErrors.push(`Row ${rowNumber}: ${emailResult.error}`);
                  }
                  if (typeResult.error) {
                    errors.type = typeResult.error;
                    validationErrors.push(`Row ${rowNumber}: ${typeResult.error}`);
                  }
                  const isLinkType = typeResult.value === 'link';
                  if (isLinkType) {
                    const linkResult = validateUploadLink(uploadlink, { required: true });
                    rowdata.uploadlink = linkResult.value;
                    if (linkResult.error) {
                      errors.uploadlink = linkResult.error;
                      validationErrors.push(`Row ${rowNumber}: ${linkResult.error}`);
                    }
                  } else if (uploadlink) {
                    errors.uploadlink = ERROR_MESSAGES.UPLOAD_LINK_NOT_ALLOWED;
                    validationErrors.push(`Row ${rowNumber}: ${ERROR_MESSAGES.UPLOAD_LINK_NOT_ALLOWED}`);
                  }
                  if (priorityResult.error) {
                    errors.priority = priorityResult.error;
                    validationErrors.push(`Row ${rowNumber}: ${priorityResult.error}`);
                  }
                  if (dueDaysResult.error) {
                    errors.duedate = dueDaysResult.error;
                    validationErrors.push(`Row ${rowNumber}: ${dueDaysResult.error}`);
                  }
                  if (taskValidation.errors.length > 0) {
                    errors.checklistref = 'Invalid task reference(s)';
                    validationErrors.push(...taskValidation.errors);
                  }
                  if (taskValidation.warnings && taskValidation.warnings.length > 0) {
                    validationErrors.push(...taskValidation.warnings);
                  }

                  filedata.push(rowdata);
                  if (Object.keys(errors).length > 0) {
                    this.errorFlag = true;
                  }
                  errordata.push(errors);
                }
              }

              this.dataErrorMessages = validationErrors;
              this.infoNotes = infoNotes;
              this.uploaddata = filedata;
              this.dataerror = errordata;
            } else {
              this.headerErrorMessages = [
                'Column headers mismatch detected:',
                `Expected headers: '${EXPECTED_HEADERS.join("', '")}'`,
                `Found headers: '${actualHeaders.join("', '")}'`,
                'Please ensure columns are in the correct order.',
              ];
              this.errorFlag = true;
              this.uploaddata = [];
              this.dataerror = [];
              this.$store.commit('sbError', 'Column headers do not match the template. Please re-download the template and try again.');
            }
          })
          .catch((err) => {
            if (parseId !== this.parseGeneration) return;
            console.log('[BulkUploadInformationManager]:', err);
            const message = err.message || err.toString() || ERROR_MESSAGES.UNKNOWN_ERROR;
            this.dataErrorMessages = [message];
            this.errorFlag = true;
            this.uploaddata = [];
            this.filesbulk = [];
            this.$store.commit('sbError', message);
          })
          .finally(() => {
            if (parseId !== this.parseGeneration) return;
            this.file = null;
            this.fileprocessing[key] = 0;
            this.loading = false;
          });
      }
    },
    uploadData() {
      if (!this.canSubmitUpload) {
        this.$store.commit('sbError', this.bulkUploadErrorFooterText);
        return;
      }
      this.loading = true;

      const apiData = this.uploaddata.map((row) => {
        const type = row._type_code || row.type;
        const existingOwner = this.findExistingInfoOwner(row.info_owner_email);
        return {
          description: row.requested_info,
          type,
          uploadlink: type === 'link' ? (row.uploadlink || '') : '',
          category: row.classification,
          info_owner_name: existingOwner ? trimInput(existingOwner.name) : row.info_owner_name,
          info_owner_email: row.info_owner_email,
          tasklist: row._tasklist || [],
          priority: row.priority,
          duedate: row.duedate,
        };
      });

      this.axios
        .post('/v2/financialclose/masters/informationmanager/bulkadd', { data: apiData })
        .then((dt) => {
          if (dt.data.status === 'success') {
            this.dialog = false;
            this.resetData();
            this.$emit('reload');
            this.$store.commit('sbSuccess', ERROR_MESSAGES.INFORMATION_REQUIREMENTS_UPLOADED);
          } else {
            if (dt.data.data && Array.isArray(dt.data.data) && dt.data.data.length > 0) {
              const errorMessages = [];
              dt.data.data.forEach((errorObj) => {
                Object.keys(errorObj).forEach((indexKey) => {
                  const rowNum = parseInt(indexKey.replace('index', ''), 10) + 1;
                  const fieldErrors = errorObj[indexKey];
                  Object.keys(fieldErrors).forEach((fieldName) => {
                    const errors = fieldErrors[fieldName];
                    if (Array.isArray(errors)) {
                      errors.forEach((errMsg) => {
                        errorMessages.push(`Row ${rowNum}: ${fieldName} - ${errMsg}`);
                      });
                    } else if (errors) {
                      errorMessages.push(`Row ${rowNum}: ${fieldName} - ${errors}`);
                    }
                  });
                });
              });
              this.dataErrorMessages = errorMessages;
            } else {
              this.dataErrorMessages = [
                dt.data.message || ERROR_MESSAGES.ERROR_UPLOADING_INFORMATION_REQUIREMENTS,
              ];
            }

            this.errorFlag = true;
            this.$store.commit(
              'sbError',
              dt.data.message || ERROR_MESSAGES.ERROR_UPLOADING_INFORMATION_REQUIREMENTS,
            );
          }
        })
        .catch((err) => {
          console.log('[BulkUploadInformationManager]:', err);
          const message = err.message || err.toString() || ERROR_MESSAGES.UNKNOWN_ERROR;
          if (!this.dataErrorMessages.length) {
            this.dataErrorMessages = [message];
          }
          this.errorFlag = true;
          this.$store.commit('sbError', message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateFilesBulk(newFilesBulk) {
      this.filesbulk = newFilesBulk;
      this.readFile('file');
    },
    resetDragDrop() {
      this.resetTrigger = false;
      this.$nextTick(() => {
        this.resetTrigger = true;
      });
    },
    reuploadDoc() {
      this.resetUploadState();
    },
    resetUploadState() {
      this.parseGeneration += 1;
      this.fileprocessing = {};
      this.loading = false;
      this.uploaddata = [];
      this.dataerror = [];
      this.errorFlag = false;
      this.headerErrorMessages = [];
      this.dataErrorMessages = [];
      this.infoNotes = [];
      this.dataOnlyWithError = false;
      this.filesbulk = [];
      this.file = null;
      this.resetDragDrop();
    },
    resetData() {
      this.resetUploadState();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './BulkUploadInformationManager.scss';
</style>
